import { useFormContext } from "react-hook-form";
import UserBadge from "./UserBadge";
import { useEffect, useMemo, useState } from "react";
import { ReactComponent as PlusSvg } from "@/assets/images/plus.svg";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import CommonInput from "@/components/Common/Input";
import { User } from "@/types/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/libs/utils";
import { ScenarioApprovalFormSchema } from "./ApprovalScenarioDialog";
import { SettingGroup, SettingUser } from "@/types/setting";
import { GetSettingGroupList, GetSettingUserList } from "@/services/Setting";

type ApprovalAccordionContentProps = {
  index: number;
  variant?: "approvers" | "references";
};

const variantMap: Record<
  "approvers" | "references",
  { label: string; size: "lg" | "sm" }
> = {
  ["approvers"]: { label: "담당", size: "lg" },
  ["references"]: { label: "참조", size: "sm" },
};

export default function ApprovalAccordionContent({
  index,
  variant = "approvers",
}: ApprovalAccordionContentProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedGroupTab, setSelectedGroupTab] = useState<string | null>(null);

  const [users, setUsers] = useState<SettingUser[]>([]);
  const [groups, setGroups] = useState<SettingGroup[]>([]);

  const { watch } = useFormContext();
  const watchedApprovalSteps = watch("approvalSteps") || [];

  function getAlreadySelectedUserIds(
    steps: ScenarioApprovalFormSchema["approvalSteps"],
    currentIndex: number
  ) {
    const selected: Set<string> = new Set();
    steps.forEach((step, i) => {
      if (i === currentIndex) return;
      step.approvers?.forEach((id) => selected.add(id));
      step.references?.forEach((id) => selected.add(id));
    });
    return selected;
  }

  const getUserList = async () => {
    try {
      const result = await GetSettingUserList({
        page_no: 1,
        page_size: 500,
      });
      setUsers(result.data.pods[0].content.items);
    } catch (e) {}
  };

  const getGroupList = async () => {
    try {
      const result = await GetSettingGroupList();
      const content = result.data.pods[0].content;
      setGroups(content);
    } catch (e) {}
  };

  const memoizedGroups = useMemo(() => {
    const map = new Map();
    for (const user of users) {
      const group = user.groups[0];
      if (!group?.group_id) continue;

      if (!map.has(group.group_id)) {
        map.set(group.group_id, {
          group_id: group.group_id,
          group_name: group.group_name,
        });
      }
    }

    return Array.from(map.values());
  }, [users]);

  const alreadySelected = useMemo(
    () => getAlreadySelectedUserIds(watchedApprovalSteps, index),
    [watchedApprovalSteps, index]
  );

  const filteredUsers = users
    .filter((user) => !alreadySelected.has(user.user_id))
    .filter(
      (user) =>
        !selectedGroupTab || user.groups[0]?.group_id === selectedGroupTab
    )
    .filter((user) => {
      if (inputValue === "") return true;
      const fullName = `${user.first_name}${user.last_name}`;
      return (
        fullName.toLowerCase().includes(inputValue.toLowerCase()) ||
        user.email?.toLowerCase().includes(inputValue.toLowerCase())
      );
    });

  const onChangePopoverOpen = (open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setInputValue("");
      setSelectedGroupTab(null);
    }
  };

  const onLoad = () => {
    getGroupList();
    getUserList();
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <div className="flex items-center gap-2 pb-2">
      <span className="text-gray-B text-sm font-semibold">
        {variantMap[variant].label}
      </span>

      {watchedApprovalSteps[index]?.[variant]?.length > 0 && (
        <div className="flex items-center gap-1">
          {watchedApprovalSteps[index]?.[variant]?.map((userId: string) => {
            const user = users.find((user) => user.user_id === userId);
            if (!user) return null;
            return (
              <UserBadge
                key={`badge-${index}-${userId}`}
                index={index}
                user={
                  {
                    user_id: user.user_id,
                    img_url: user.image_url,
                    user_name: `${user.first_name}${user.last_name}`,
                    // position: user.groups[0]?.position_name,
                  } as User
                }
                variant={variant}
                size={variantMap[variant].size}
                noPosition={variant === "references"}
              />
            );
          })}
        </div>
      )}

      <Popover open={popoverOpen} onOpenChange={onChangePopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            className={cn("px-2 py-[6px] border-gray-D border rounded-2xl", {
              "h-8 w-20": variant === "approvers",
              "h-7 w-16": variant === "references",
            })}
            variant="outline"
          >
            <PlusSvg className="size-3 text-gray-C" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-64 w-auto max-w-[692px] max-h-64 overflow-y-auto p-[10px] flex flex-col justify-between gap-2">
          <div className="flex flex-col gap-2">
            <CommonInput
              type="text"
              placeholder="부서원을 검색하세요"
              defaultValue={inputValue}
              onChange={(value) => setInputValue(value)}
              onKeyDownEnter={() => {}}
            />
            <div className="flex items-center gap-1">
              <span className="shrink-0 text-[10px] text-gray-B font-semibold">
                부서
              </span>
              {memoizedGroups.map(({ group_id, group_name }, index) => (
                <Button
                  key={`group-${index}`}
                  size="sm"
                  className={cn(
                    "text-[10px] font-semibold rounded-2xl",
                    {
                      "bg-dark-red hover:bg-dark-red/80 text-white":
                        group_id === selectedGroupTab,
                    },
                    {
                      "bg-gray-E hover:bg-gray-E/80 text-gray-B":
                        group_id !== selectedGroupTab,
                    }
                  )}
                  onClick={() =>
                    setSelectedGroupTab((prev) =>
                      prev === group_id ? null : group_id
                    )
                  }
                >
                  {group_name}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-auto">
            {filteredUsers.map((user) => (
              <PopoverUserRow
                key={user.user_id}
                user={user}
                index={index}
                variant={variant}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function PopoverUserRow({
  user,
  index,
  variant,
}: {
  user: SettingUser;
  index: number;
  variant: "approvers" | "references";
}) {
  const { setValue, watch } = useFormContext();
  const watchedApprovalSteps = watch("approvalSteps", []);

  const onClickRow = () => {
    const currentArr = watchedApprovalSteps[index]?.[variant] || [];

    const updatedArr = currentArr.includes(user.user_id)
      ? currentArr.filter((id: string) => id !== user.user_id)
      : [...currentArr, user.user_id];

    setValue(`approvalSteps.${index}.${variant}`, updatedArr);
  };

  return (
    <div
      className={cn(
        "h-7 py-2 px-[10px] flex justify-between hover:bg-gray-E cursor-pointer",
        {
          "bg-dark-red/20": watchedApprovalSteps[index]?.[variant]?.includes(
            user.user_id
          ),
        }
      )}
      onClick={onClickRow}
    >
      <div className="flex items-center gap-2">
        <Avatar className="size-3">
          <AvatarImage src={user.image_url} />
          <AvatarFallback>P</AvatarFallback>
        </Avatar>
        <span className="text-[10px] text-gray-A font-semibold">
          {user.first_name}
          {user.last_name}
        </span>
        <span className="text-[10px] text-dark-red font-semibold">
          {user.groups[0]?.group_name}
        </span>
      </div>
      <div className="flex items-center text-[8px] text-gray-B">
        {user.email}
      </div>
    </div>
  );
}
